import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Button, ButtonProps, useTheme } from '@mui/material';
import React from 'react';

interface LensButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'danger';
  buttonSize?: 'medium' | 'small';
}

export const LensButton: React.FC<LensButtonProps> = ({
  variant = 'primary',
  buttonSize = 'medium',
  children,
  sx,
  ...props
}) => {
  const { tokens } = useTheme<LensTheme>();

  const isPrimary = variant === 'primary';
  const isDanger = variant === 'danger';
  const isMedium = buttonSize === 'medium';

  const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textTransform: 'none' as const,
    boxShadow: 'none',
    '&:hover': {
      cursor: 'pointer',
      boxShadow: 'none',
    },
    '&:active': {
      boxShadow: 'none',
    },
  };

  const primaryStyles = {
    borderRadius: tokens.semantic.radius.lg.value,
    border: `1px solid ${tokens.semantic.color.action.primary.default.value}`,
    color: tokens.semantic.color.action.primary.onPrimary.value,
    backgroundColor: tokens.semantic.color.action.primary.default.value,
    '&:hover': {
      ...baseStyles['&:hover'],
      color: `${tokens.semantic.color.action.primary.onPrimary.value} !important`,
      backgroundColor: `${tokens.semantic.color.action.primary.hover.value} !important`,
    },
    '&:active': {
      ...baseStyles['&:active'],
      backgroundColor: tokens.semantic.color.action.primary.active.value,
    },
    '&.Mui-disabled': {
      backgroundColor: `${tokens.semantic.color.action.primary.disabled.value} !important`,
      borderColor: `${tokens.semantic.color.action.primary.disabled.value} !important`,
      color: `${tokens.semantic.color.type.disabled.value} !important`,
    },
  };

  const secondaryStyles = {
    borderRadius: tokens.semantic.radius.md.value,
    border: `1px solid ${tokens.semantic.color.action.secondary.outline.value}`,
    color: tokens.semantic.color.action.secondary.onSecondary.value,
    backgroundColor: tokens.semantic.color.surface.default.value,
    '&:hover': {
      ...baseStyles['&:hover'],
      backgroundColor: tokens.semantic.color.action.secondary.hoverFill.value,
    },
    '&:active': {
      ...baseStyles['&:active'],
      backgroundColor: tokens.semantic.color.action.secondary.activeFill.value,
    },
    '&.Mui-disabled': {
      borderColor: `${tokens.semantic.color.action.secondary.disabledOutline.value} !important`,
      color: `${tokens.semantic.color.type.disabled.value} !important`,
    },
  };

  const dangerStyles = {
    borderRadius: tokens.semantic.radius.md.value,
    border: `1px solid ${tokens.semantic.color.status.error.text.value}`,
    color: tokens.semantic.color.status.error.text.value,
    backgroundColor: tokens.semantic.color.surface.default.value,
    '&:hover': {
      ...baseStyles['&:hover'],
      color: `${tokens.semantic.color.status.error.text.value} !important`,
      backgroundColor: `${tokens.semantic.color.status.error.background.value} !important`,
      borderColor: `${tokens.semantic.color.status.error.text.value} !important`,
    },
    '&:active': {
      ...baseStyles['&:active'],
      color: `${tokens.semantic.color.status.error.text.value} !important`,
      backgroundColor: `${tokens.semantic.color.status.error.background.value} !important`,
    },
  };

  const mediumSizeStyles = {
    width: 'auto',
    height: '40px',
    padding: `${tokens.core.spacing[1].value} ${tokens.core.spacing['1_5'].value}`,
    '& .MuiButton-label, & .MuiButton-text, &': {
      fontFamily: tokens.semantic.font.text.body.fontFamily.value,
      fontSize: `${tokens.semantic.font.text.body.fontSize.value}`,
      fontWeight: tokens.semantic.fontWeight.emphasis.value,
      letterSpacing: `${tokens.semantic.font.text.body.letterSpacing.value}`,
      lineHeight: `${tokens.semantic.font.text.body.lineHeight.value}`,
    },
  };

  const smallSizeStyles = {
    width: 'auto',
    height: '32px',
    padding: `${tokens.core.spacing['0_5'].value} ${tokens.core.spacing['1_5'].value}`,
    '& .MuiButton-label, & .MuiButton-text, &': {
      fontSize: `${tokens.component.button.small.fontSize.value}`,
      fontWeight: tokens.semantic.fontWeight.emphasis.value,
      letterSpacing: `${tokens.component.button.small.letterSpacing.value}`,
      lineHeight: `${tokens.component.button.small.lineHeight.value}`,
    },
  };

  return (
    <Button
      {...props}
      sx={{
        ...baseStyles,
        ...(isPrimary
          ? primaryStyles
          : isDanger
            ? dangerStyles
            : secondaryStyles),
        ...(isMedium ? mediumSizeStyles : smallSizeStyles),
        ...sx,
      }}
    >
      {children}
    </Button>
  );
};
